import React from "react";
import { Box } from "@chakra-ui/react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeRaw from "rehype-raw";
import "katex/dist/katex.min.css";
import TooltipComponent from "./components/ToolTip";

export type IMarkDownContext =
  | "quiz-question"
  | "root"
  | "defination"
  | "explanation"
  | "quiz-option"
  | "quiz-hint"
  | undefined;

export type IMarkdownProps = {
  content: string;
  color?: string;
};

const MarkDownRender: React.FC<IMarkdownProps> = ({
  content,
  color = "white",
}: IMarkdownProps) => {
  return (
    <Box
      sx={{
        "& .markdown-body": {
          fontFamily: '"Open Sans", sans-serif',
          lineHeight: 1.3,
          width: "100%",
          fontSize: "1rem",
          textAlign: "left",
          color,

          "& small": {
            color: "whiteAlpha.800",
          },

          "& li": {
            my: ".8rem",
          },

          "& h1, & h2, & h3, & h4, & h5, & h6": {
            marginTop: "16px",
            marginBottom: "10px",
            fontWeight: 600,
            color: "white",
            lineHeight: 1.25,
          },

          "& h1": { fontSize: "1.5rem" },
          "& h2": { fontSize: "1.5em", paddingBottom: "0.3em" },
          "& h3": { fontSize: "1.25em" },
          "& h4": { fontSize: "1em" },
          "& h5": { fontSize: "0.875em" },
          "& h6": { fontSize: "0.85em", color: "gray.600" },

          "& p": {
            marginTop: "4px",
            marginBottom: "8px",
            whiteSpace: "pre-wrap",
            lineHeight: 1.6,
          },

          "& .small-text": {
            fontSize: "0.875rem",
            color: "gray.700",
          },

          "& a": {
            color: "blue.500",
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          },

          "& strong": { fontWeight: "bold" },
          "& em": { fontStyle: "italic" },

          "& code": {
            fontFamily: "monospace",
            padding: "0.1rem 0.2em",
            color: "black",
            borderRadius: ".2rem",
            backgroundColor: "#C6C6C6",
            fontSize: { base: ".9rem", lg: "1rem" },
          },

          "& pre": {
            fontFamily: "monospace",
            overflow: "auto",
            fontSize: "1rem",
            lineHeight: 1.45,
            color: "white",
            borderRadius: "6px",
          },

          "& ul, & ol": {
            paddingLeft: "2em",
            marginTop: 0,
            marginBottom: "16px",
          },

          "& blockquote": {
            padding: "0 1em",
            color: "gray.600",
            borderLeft: "0.25em solid",
            borderColor: "gray.300",
          },

          "& table": {
            width: "100%",
            textAlign: "left",
            borderCollapse: "collapse",
            marginTop: 0,
            marginBottom: "16px",
            backgroundColor: "background",
            color: "#C6C6C6",
            border: "1px solid",
            borderColor: "whiteAlpha.400",

            "& th": {
              backgroundColor: "whiteAlpha.300", // Optional: Light background for headers
              padding: "0.75rem",
              fontWeight: "bold",
            },

            "& th, & td": {
              padding: "0.5rem 0.5rem",
              border: "1px solid",
              borderColor: "whiteAlpha.400",
            },

            "& tr": {
              borderTop: "1px solid",
              borderColor: "whiteAlpha.400",
              "&:hover": {
                backgroundColor: "whiteAlpha.200", // Optional: Hover effect
              },
            },
          },
        },
      }}
    >
      <ReactMarkdown
        className="markdown-body"
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[rehypeKatex, rehypeRaw]}
        components={{
          a: TooltipComponent,
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkDownRender;
